import cn from 'classnames';
import { type ChangeEvent, type FC, useCallback, useId } from 'react';

import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';

import Icon from '@/components/UI/Icon';
import { errorToast, formatBytes, trunc } from '@/helpers';
import { useFileDropzone } from '@/support/Hooks/dropzone/useFileDropzone';

interface FileUploadProps {
  limit?: number;
  files: Array<File>;
  setFiles: (files: Array<File>) => void;
  disabled?: boolean;
  allowedExtensions?: Array<string>;
  /**
   * When true the picker accepts more than one file at a time and each pick/drop *appends* to the
   * current selection (rather than replacing it), so the drop target stays available alongside the
   * list of already-selected files. Defaults to false to preserve single-file consumers.
   */
  multiple?: boolean;
  testId?: string;
}

const hasValidExtension = (fileName: string, allowedExtensions: Array<string>): boolean => {
  if (_isEmpty(allowedExtensions)) {
    return true;
  }
  const match = /\.([^.]+)$/.exec(fileName);
  if (match === null) {
    return false;
  }
  return allowedExtensions.includes(`.${match[1].trim()}`);
};

const FileUpload: FC<FileUploadProps> = ({
  limit,
  files,
  setFiles,
  disabled = false,
  allowedExtensions = [],
  multiple = false,
  testId = 'file-upload-drop-zone',
}) => {
  // Multi-file consumers keep the drop target visible beside the list so more files can be added
  // after the first pick; single-file consumers swap the target out for the filled field as before.
  const showDropTarget = multiple || _isEmpty(files);
  return (
    <>
      {_isNotEmpty(files) && <FilledField setFiles={setFiles} files={files} />}
      {showDropTarget && (
        <FileDropTarget
          limit={limit}
          files={files}
          setFiles={setFiles}
          disabled={disabled}
          allowedExtensions={allowedExtensions}
          multiple={multiple}
          testId={testId}
        />
      )}
    </>
  );
};

interface FileDropTargetProps {
  limit?: number;
  files: Array<File>;
  setFiles: (files: Array<File>) => void;
  disabled: boolean;
  allowedExtensions: Array<string>;
  multiple: boolean;
  testId: string;
}

const FileDropTarget: FC<FileDropTargetProps> = ({
  limit,
  files,
  setFiles,
  disabled,
  allowedExtensions,
  multiple,
  testId,
}) => {
  const applyPickedFiles = useCallback(
    (fileList: FileList | null) => {
      if (fileList === null || fileList.length === 0) {
        return;
      }
      const picked = Array.from(fileList);
      if (_isNotEmpty(allowedExtensions)) {
        const invalid = picked.find((f) => {
          return !hasValidExtension(f.name, allowedExtensions);
        });
        if (_notNil(invalid)) {
          errorToast(
            <>
              <p>
                <b>{invalid.name}</b>
              </p>
              <p>is not a supported file type</p>
            </>
          );
          return;
        }
      }
      let next = multiple ? [...files, ...picked] : picked;
      if (_notNil(limit) && limit > 0) {
        next = next.slice(0, limit);
      }
      setFiles(next);
    },
    [allowedExtensions, files, limit, multiple, setFiles]
  );

  const { isDraggingOver, dropzoneProps } = useFileDropzone({ onFiles: applyPickedFiles, disabled });
  const isActive = !disabled && isDraggingOver;

  return (
    <div
      data-testid={testId}
      className={cn('ui__file__upload ba b--dashed f6 tc', {
        ui__disabled: disabled,
        'b--blue': isActive,
        'b--moon-gray': !isActive,
      })}
      {...dropzoneProps}
    >
      <p className="ma0">
        Drag and drop or{' '}
        <ChooseFileLink
          files={files}
          setFiles={setFiles}
          allowedExtensions={allowedExtensions}
          limit={limit}
          multiple={multiple}
          disabled={disabled}
        />
      </p>
    </div>
  );
};

interface ChooseFileLinkProps {
  files: Array<File>;
  setFiles: (files: Array<File>) => void;
  allowedExtensions: Array<string>;
  limit?: number;
  multiple: boolean;
  disabled: boolean;
}

const ChooseFileLink: FC<ChooseFileLinkProps> = ({ files, setFiles, allowedExtensions, limit, multiple, disabled }) => {
  const inputId = useId();

  const captureFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (list === null || list.length === 0) {
      return;
    }
    const picked = Array.from(list);
    if (_isNotEmpty(allowedExtensions)) {
      const invalid = picked.find((f) => {
        return !hasValidExtension(f.name, allowedExtensions);
      });
      if (_notNil(invalid)) {
        errorToast(
          <>
            <p>
              <b>{invalid.name}</b>
            </p>
            <p>is not a supported file type</p>
          </>
        );
        e.target.value = '';
        return;
      }
    }
    let next = multiple ? [...files, ...picked] : picked;
    if (_notNil(limit) && limit > 0) {
      next = next.slice(0, limit);
    }
    setFiles(next);
    e.target.value = '';
  };

  return (
    <label htmlFor={inputId} className="link dib pointer ma0">
      browse your computer.
      <input
        id={inputId}
        type="file"
        className="dn"
        disabled={disabled}
        multiple={multiple}
        onChange={captureFiles}
        accept={_isNotEmpty(allowedExtensions) ? allowedExtensions.join(',') : undefined}
      />
    </label>
  );
};

interface FilledFieldProps {
  files: Array<File>;
  setFiles: (files: Array<File>) => void;
}

const FilledField: FC<FilledFieldProps> = ({ files, setFiles }) => {
  const handleRemoveFile = (index: number) => {
    setFiles(
      files.filter((_, i) => {
        return i !== index;
      })
    );
  };

  return (
    <ul className="ui__file__upload__filledField list ma0">
      {!_isEmpty(files) &&
        files.map((f, i) => (
          <li
            className={cn('flex justify-between items-center', { mt2: i !== 0 })}
            key={`${f.name}-${String(f.lastModified)}-${String(i)}`}
          >
            <span className="basier-med bg-light-silver br2 mr2 near-black f6 w-100">
              {trunc(f.name, 20)} ({formatBytes(f.size)})
            </span>
            <button
              type="button"
              className="pointer hover-near-black bn bg-transparent pa0"
              onClick={() => {
                handleRemoveFile(i);
              }}
            >
              <Icon icon="close" width="10" height="10" className="dark-gray" />
            </button>
          </li>
        ))}
    </ul>
  );
};

export default FileUpload;
